<template>
  <div class="admin-dash">
    <!-- Toast -->
    <transition name="toast">
      <div v-if="toast.show" class="admin-dash__toast" :class="'admin-dash__toast--' + toast.type">
        {{ toast.msg }}
      </div>
    </transition>

    <div class="admin-dash__header">
      <h1 class="admin-dash__title">Painel de Conteúdos</h1>
      <div class="admin-dash__controls">
        <input
          v-model="searchQuery"
          type="text"
          class="admin-dash__search"
          placeholder="Buscar ferramenta..."
        />
        <select v-model="selectedLang" class="admin-dash__select" @change="loadAll">
          <option value="pt">Português</option>
          <option value="en">English</option>
          <option value="es">Español</option>
          <option value="nl">Nederlands</option>
        </select>
        <button class="admin-dash__logout" @click="logout">Sair</button>
      </div>
    </div>

    <div class="admin-dash__toolbar">
      <button class="admin-dash__btn admin-dash__btn--new" @click="openToolForm()">+ Nova Ferramenta</button>
    </div>

    <div v-if="isLoading" class="admin-dash__loading">Carregando ferramentas...</div>

    <div v-else class="admin-dash__pilares">
      <div
        v-for="pilar in filteredPilares"
        :key="pilar.id"
        class="admin-dash__pilar"
      >
        <div class="admin-dash__pilar-header" @click="pilar.open = !pilar.open">
          <div class="admin-dash__pilar-header-left">
            <h2>{{ pilar.nome }}. {{ pilar.descricao }}</h2>
            <span v-if="!pilar.versao?.conteudo" class="admin-dash__badge admin-dash__badge--empty">Sem conteúdo</span>
          </div>
          <span class="admin-dash__chevron" :class="{ 'admin-dash__chevron--open': pilar.open }">&#9660;</span>
        </div>

        <div v-if="pilar.open" class="admin-dash__pilar-body">
          <!-- Conteúdo do pilar (nível 0) -->
          <div class="admin-dash__versao-card">
            <div class="admin-dash__card-header">
              <strong>Texto do pilar (Nível 0)</strong>
              <span v-if="pilar.versao?.tempo_leitura_min" class="admin-dash__reading-time">
                {{ pilar.versao.tempo_leitura_min }} min
              </span>
            </div>
            <div v-if="pilar.editingPilar">
              <div class="admin-dash__editor-split">
                <div class="admin-dash__editor-pane">
                  <div class="admin-dash__pane-label">Editor</div>
                  <RichTextEditor v-model="pilar.editConteudo" />
                </div>
                <div class="admin-dash__preview-pane">
                  <div class="admin-dash__pane-label">Preview</div>
                  <div class="admin-dash__preview-content" v-html="pilar.editConteudo"></div>
                </div>
              </div>
              <div class="admin-dash__actions">
                <button class="admin-dash__btn admin-dash__btn--save" @click="saveVersao(pilar)" :disabled="pilar.saving">
                  {{ pilar.saving ? 'Salvando...' : 'Salvar' }}
                </button>
                <button class="admin-dash__btn admin-dash__btn--cancel" @click="pilar.editingPilar = false">Cancelar</button>
              </div>
            </div>
            <div v-else>
              <p class="admin-dash__excerpt">{{ pilar.versao?.excerpt || 'Sem excerpt' }}</p>
              <div class="admin-dash__btn-row">
                <button class="admin-dash__btn" @click="startEditPilar(pilar)">Editar</button>
                <button class="admin-dash__btn admin-dash__btn--outline" @click="openHistory(pilar)">Histórico</button>
                <button class="admin-dash__btn admin-dash__btn--outline" @click="openTranslations(pilar)">Traduções</button>
                <button class="admin-dash__btn admin-dash__btn--outline" @click="openToolForm(pilar)">&#9998; Ferramenta</button>
              </div>
            </div>
          </div>

          <!-- Subgrupos nível 1 -->
          <div
            v-for="sub1 in filterSubs(pilar.subgrupos)"
            :key="sub1.id"
            class="admin-dash__sub1"
          >
            <div class="admin-dash__sub1-header" @click="sub1.open = !sub1.open">
              <div class="admin-dash__sub1-header-left">
                <h3>{{ sub1.nome }}. {{ sub1.descricao }}</h3>
                <span v-if="!sub1.versao?.conteudo" class="admin-dash__badge admin-dash__badge--empty">Sem conteúdo</span>
              </div>
              <span class="admin-dash__chevron" :class="{ 'admin-dash__chevron--open': sub1.open }">&#9660;</span>
            </div>

            <div v-if="sub1.open" class="admin-dash__sub1-body">
              <div class="admin-dash__versao-card">
                <div class="admin-dash__card-header">
                  <strong>Texto do subgrupo (Nível -1)</strong>
                  <span v-if="sub1.versao?.tempo_leitura_min" class="admin-dash__reading-time">
                    {{ sub1.versao.tempo_leitura_min }} min
                  </span>
                </div>
                <div v-if="sub1.editing">
                  <div class="admin-dash__editor-split">
                    <div class="admin-dash__editor-pane">
                      <div class="admin-dash__pane-label">Editor</div>
                      <RichTextEditor v-model="sub1.editConteudo" />
                    </div>
                    <div class="admin-dash__preview-pane">
                      <div class="admin-dash__pane-label">Preview</div>
                      <div class="admin-dash__preview-content" v-html="sub1.editConteudo"></div>
                    </div>
                  </div>
                  <div class="admin-dash__actions">
                    <button class="admin-dash__btn admin-dash__btn--save" @click="saveVersao(sub1)" :disabled="sub1.saving">
                      {{ sub1.saving ? 'Salvando...' : 'Salvar' }}
                    </button>
                    <button class="admin-dash__btn admin-dash__btn--cancel" @click="sub1.editing = false">Cancelar</button>
                  </div>
                </div>
                <div v-else>
                  <p class="admin-dash__excerpt">{{ sub1.versao?.excerpt || 'Sem excerpt' }}</p>
                  <div class="admin-dash__btn-row">
                    <button class="admin-dash__btn" @click="startEdit(sub1)">Editar</button>
                    <button class="admin-dash__btn admin-dash__btn--outline" @click="openHistory(sub1)">Histórico</button>
                    <button class="admin-dash__btn admin-dash__btn--outline" @click="openTranslations(sub1)">Traduções</button>
                    <button class="admin-dash__btn admin-dash__btn--outline" @click="openToolForm(sub1)">&#9998; Ferramenta</button>
                  </div>
                </div>
              </div>

              <!-- Ferramentas nível 2 -->
              <div
                v-for="sub2 in filterSubs(sub1.subgrupos)"
                :key="sub2.id"
                class="admin-dash__sub2"
              >
                <div class="admin-dash__versao-card">
                  <div class="admin-dash__card-header">
                    <div class="admin-dash__card-header-left">
                      <strong>{{ sub2.nome }}. {{ sub2.descricao }}</strong>
                      <span v-if="!sub2.versao?.conteudo" class="admin-dash__badge admin-dash__badge--empty">Sem conteúdo</span>
                    </div>
                    <span v-if="sub2.versao?.tempo_leitura_min" class="admin-dash__reading-time">
                      {{ sub2.versao.tempo_leitura_min }} min
                    </span>
                  </div>
                  <div v-if="sub2.editing">
                    <div class="admin-dash__editor-split">
                      <div class="admin-dash__editor-pane">
                        <div class="admin-dash__pane-label">Editor</div>
                        <RichTextEditor v-model="sub2.editConteudo" />
                      </div>
                      <div class="admin-dash__preview-pane">
                        <div class="admin-dash__pane-label">Preview</div>
                        <div class="admin-dash__preview-content" v-html="sub2.editConteudo"></div>
                      </div>
                    </div>
                    <div class="admin-dash__actions">
                      <button class="admin-dash__btn admin-dash__btn--save" @click="saveVersao(sub2)" :disabled="sub2.saving">
                        {{ sub2.saving ? 'Salvando...' : 'Salvar' }}
                      </button>
                      <button class="admin-dash__btn admin-dash__btn--cancel" @click="sub2.editing = false">Cancelar</button>
                    </div>
                  </div>
                  <div v-else>
                    <p class="admin-dash__excerpt">{{ sub2.versao?.excerpt || 'Sem excerpt' }}</p>
                    <div class="admin-dash__btn-row">
                      <button class="admin-dash__btn" @click="startEdit(sub2)">Editar</button>
                      <button class="admin-dash__btn admin-dash__btn--outline" @click="openHistory(sub2)">Histórico</button>
                      <button class="admin-dash__btn admin-dash__btn--outline" @click="openTranslations(sub2)">Traduções</button>
                      <button class="admin-dash__btn admin-dash__btn--outline" @click="openToolForm(sub2)">&#9998; Ferramenta</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <!-- eslint-disable vue/no-unused-components -->
    <HistoryModal
      v-if="historyModal.show"
      :versaoId="historyModal.versaoId"
      @close="historyModal.show = false"
      @restored="(conteudo) => onHistoryRestored(conteudo)"
    />

    <TranslationsModal
      v-if="translationsModal.show"
      :ferramentaId="translationsModal.ferramentaId"
      :ferramentaNome="translationsModal.ferramentaNome"
      @close="translationsModal.show = false"
    />

    <ToolFormModal
      v-if="toolFormModal.show"
      :ferramenta="toolFormModal.ferramenta"
      :allFerramentas="allFerramentasFlat"
      :defaultLang="selectedLang"
      @close="toolFormModal.show = false"
      @saved="onToolSaved"
      @deleted="onToolDeleted"
    />
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import api from '@/api';
import RichTextEditor from '@/components/RichTextEditor/index.vue';
import HistoryModal from '@/components/HistoryModal/index.vue';
import TranslationsModal from '@/components/TranslationsModal/index.vue';
import ToolFormModal from '@/components/ToolFormModal/index.vue';

export default {
  name: 'AdminDashboard',
  components: { RichTextEditor, HistoryModal, TranslationsModal, ToolFormModal },
  setup() {
    const router = useRouter();
    const selectedLang = ref('pt');
    const isLoading = ref(false);
    const pilares = ref([]);
    const searchQuery = ref('');
    const toast = reactive({ show: false, msg: '', type: 'success' });

    // Modal states
    const historyModal = reactive({ show: false, versaoId: null });
    const translationsModal = reactive({ show: false, ferramentaId: null, ferramentaNome: '' });
    const toolFormModal = reactive({ show: false, ferramenta: null });
    const allFerramentasFlat = ref([]);

    let toastTimer = null;
    const showToast = (msg, type = 'success') => {
      toast.msg = msg;
      toast.type = type;
      toast.show = true;
      if (toastTimer) clearTimeout(toastTimer);
      toastTimer = setTimeout(() => { toast.show = false; }, 3000);
    };

    const matchesSearch = (item) => {
      if (!searchQuery.value) return true;
      const q = searchQuery.value.toLowerCase();
      return (
        (item.nome && item.nome.toLowerCase().includes(q)) ||
        (item.descricao && item.descricao.toLowerCase().includes(q))
      );
    };

    const filterSubs = (subs) => {
      if (!searchQuery.value) return subs;
      return subs.filter(s => {
        if (matchesSearch(s)) return true;
        if (s.subgrupos) return s.subgrupos.some(s2 => matchesSearch(s2));
        return false;
      });
    };

    const filteredPilares = computed(() => {
      if (!searchQuery.value) return pilares.value;
      return pilares.value.filter(p => {
        if (matchesSearch(p)) return true;
        return p.subgrupos.some(s1 => {
          if (matchesSearch(s1)) return true;
          if (s1.subgrupos) return s1.subgrupos.some(s2 => matchesSearch(s2));
          return false;
        });
      });
    });

    const logout = () => {
      sessionStorage.removeItem('admin_token');
      router.push({ name: 'AdminLogin' });
    };

    const loadVersao = async (item) => {
      try {
        const res = await api.getConteudoVersoesPorFerramenta(item.id, selectedLang.value);
        if (res.data && res.data.length > 0) {
          item.versao = res.data[0];
        } else {
          item.versao = null;
        }
      } catch {
        item.versao = null;
      }
    };

    const loadAll = async () => {
      isLoading.value = true;
      try {
        const res = await api.getTools({ idioma: selectedLang.value });
        const all = res.data || [];
        allFerramentasFlat.value = all;

        // Nível 0 = pilares
        const nivel0 = all
          .filter(t => t.nivel === 0)
          .sort((a, b) => a.nome.localeCompare(b.nome))
          .map(t => reactive({
            ...t,
            open: false,
            editingPilar: false,
            editConteudo: '',
            saving: false,
            versao: null,
            subgrupos: [],
          }));

        // Carregar versões e subgrupos de cada pilar
        for (const pilar of nivel0) {
          await loadVersao(pilar);

          // Nível 1
          const subs1 = all
            .filter(t => t.nivel === 1 && t.ferramenta_pai_id === pilar.id)
            .sort((a, b) => a.nome.localeCompare(b.nome))
            .map(t => reactive({
              ...t,
              open: false,
              editing: false,
              editConteudo: '',
              saving: false,
              versao: null,
              subgrupos: [],
            }));

          for (const sub1 of subs1) {
            await loadVersao(sub1);

            // Nível 2
            const subs2 = all
              .filter(t => t.nivel === 2 && t.ferramenta_pai_id === sub1.id)
              .sort((a, b) => a.nome.localeCompare(b.nome))
              .map(t => reactive({
                ...t,
                editing: false,
                editConteudo: '',
                saving: false,
                versao: null,
              }));

            for (const sub2 of subs2) {
              await loadVersao(sub2);
            }

            sub1.subgrupos = subs2;
          }

          pilar.subgrupos = subs1;
        }

        pilares.value = nivel0;
      } catch (err) {
        console.error('Erro ao carregar ferramentas:', err);
      } finally {
        isLoading.value = false;
      }
    };

    const startEditPilar = (pilar) => {
      pilar.editConteudo = pilar.versao?.conteudo || '';
      pilar.editingPilar = true;
    };

    const startEdit = (item) => {
      item.editConteudo = item.versao?.conteudo || '';
      item.editing = true;
    };

    const saveVersao = async (item) => {
      if (!item.versao?.id) return;
      item.saving = true;
      try {
        await api.updateVersao(item.versao.id, { conteudo: item.editConteudo });
        item.versao.conteudo = item.editConteudo;
        item.editing = false;
        item.editingPilar = false;
        showToast('Conteúdo salvo com sucesso!');
      } catch (err) {
        showToast('Erro ao salvar: ' + (err.response?.data?.message || err.message), 'error');
      } finally {
        item.saving = false;
      }
    };

    // Modal handlers
    const openHistory = (item) => {
      if (!item.versao?.id) return;
      historyModal.versaoId = item.versao.id;
      historyModal.show = true;
    };

    const onHistoryRestored = (conteudo, item) => {
      if (item && item.versao) {
        item.versao.conteudo = conteudo;
      }
      historyModal.show = false;
      showToast('Versão restaurada com sucesso!');
      loadAll();
    };

    const openTranslations = (item) => {
      translationsModal.ferramentaId = item.id;
      translationsModal.ferramentaNome = `${item.nome}. ${item.descricao}`;
      translationsModal.show = true;
    };

    const openToolForm = (ferramenta = null) => {
      toolFormModal.ferramenta = ferramenta;
      toolFormModal.show = true;
    };

    const onToolSaved = () => {
      toolFormModal.show = false;
      showToast('Ferramenta salva com sucesso!');
      loadAll();
    };

    const onToolDeleted = () => {
      toolFormModal.show = false;
      showToast('Ferramenta excluída.');
      loadAll();
    };

    onMounted(loadAll);

    return {
      selectedLang,
      isLoading,
      pilares,
      filteredPilares,
      searchQuery,
      toast,
      logout,
      loadAll,
      startEditPilar,
      startEdit,
      saveVersao,
      filterSubs,
      // Modals
      historyModal,
      translationsModal,
      toolFormModal,
      allFerramentasFlat,
      openHistory,
      onHistoryRestored,
      openTranslations,
      openToolForm,
      onToolSaved,
      onToolDeleted,
    };
  },
};
</script>

<style scoped src="./style.css"></style>
